/* TilliesToys theme behaviour: hero slider + product carousels */
if (!customElements.get('tt-slider')) {
  customElements.define(
    'tt-slider',
    class TTSlider extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('.tt-slide')];
        this.dots = [...this.querySelectorAll('[data-dot]')];
        if (this.slides.length < 2) return;
        this.index = 0;
        this.speed = (parseInt(this.dataset.speed, 10) || 6) * 1000;
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
        this.dots.forEach((d) => d.addEventListener('click', () => this.go(parseInt(d.dataset.dot, 10), true)));
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        let x0 = null;
        this.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
        this.addEventListener('touchend', (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
          x0 = null;
        });
        this.start();
      }
      disconnectedCallback() {
        this.stop();
      }
      start() {
        if (this.dataset.autoplay !== 'true' || this.reduced || this.slides.length < 2) return;
        this.stop();
        this.timer = setInterval(() => this.go(this.index + 1), this.speed);
      }
      stop() {
        clearInterval(this.timer);
      }
      go(i, user) {
        const n = this.slides.length;
        this.index = (i + n) % n;
        this.slides.forEach((s, k) => {
          const on = k === this.index;
          s.classList.toggle('is-active', on);
          s.setAttribute('aria-hidden', on ? 'false' : 'true');
          s.querySelectorAll('a').forEach((a) => (on && !a.classList.contains('tt-slide__cover') ? a.removeAttribute('tabindex') : a.setAttribute('tabindex', '-1')));
        });
        this.dots.forEach((d, k) => d.classList.toggle('is-active', k === this.index));
        if (user) this.start();
      }
    }
  );
}

if (!customElements.get('tt-rail')) {
  customElements.define(
    'tt-rail',
    class TTRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.tt-rail__track');
        const wrap = this.closest('.page-width');
        this.prev = wrap?.querySelector('[data-rail-prev]');
        this.next = wrap?.querySelector('[data-rail-next]');
        this.prev?.addEventListener('click', () => this.scrollByPage(-1));
        this.next?.addEventListener('click', () => this.scrollByPage(1));
        this.track.addEventListener('scroll', () => this.update(), { passive: true });
        window.addEventListener('resize', () => this.update());
        this.update();
      }
      scrollByPage(dir) {
        this.track.scrollBy({ left: dir * this.track.clientWidth * 0.9, behavior: 'smooth' });
      }
      update() {
        const t = this.track;
        if (!t || !this.prev) return;
        this.prev.disabled = t.scrollLeft <= 2;
        this.next.disabled = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
      }
    }
  );
}

/* ---------------- Product page: gallery ---------------- */
if (!customElements.get('tt-gallery')) {
  customElements.define(
    'tt-gallery',
    class TTGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.tt-gal__track');
        this.slides = [...this.querySelectorAll('.tt-gal__slide')];
        this.thumbs = [...this.querySelectorAll('.tt-gal__thumb')];
        this.dots = [...this.querySelectorAll('.tt-gal__dots span')];
        this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(parseInt(t.dataset.index, 10))));
        this.track.addEventListener('scroll', () => {
          clearTimeout(this.t);
          this.t = setTimeout(() => this.mark(Math.round(this.track.scrollLeft / this.track.clientWidth)), 60);
        }, { passive: true });
        // hover zoom (desktop, fine pointer only)
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.slides.forEach((s) => {
            const img = s.querySelector('img');
            if (!img) return;
            s.addEventListener('mousemove', (e) => {
              const r = s.getBoundingClientRect();
              img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
              s.classList.add('is-zoom');
            });
            s.addEventListener('mouseleave', () => s.classList.remove('is-zoom'));
          });
        }
      }
      go(i) {
        this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' });
        this.mark(i);
      }
      mark(i) {
        this.thumbs.forEach((t, k) => { t.classList.toggle('is-active', k === i); t.setAttribute('aria-selected', k === i); });
        this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      }
    }
  );
}

/* ---------------- Product page: tabs (accordion-like on mobile) ---------------- */
if (!customElements.get('tt-tabs')) {
  customElements.define(
    'tt-tabs',
    class TTTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[data-tab]')];
        this.panels = [...this.querySelectorAll('[data-panel]')];
        this.tabs.forEach((t) => t.addEventListener('click', () => this.show(t.dataset.tab)));
      }
      show(i) {
        this.tabs.forEach((t) => { const on = t.dataset.tab === i; t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on); });
        this.panels.forEach((p) => { const on = p.dataset.panel === i; p.classList.toggle('is-active', on); p.hidden = !on; });
      }
    }
  );
}

/* ---------------- Product page: sticky basket bar (shows when main button is off-screen) ---------------- */
if (!customElements.get('tt-sticky-atc')) {
  customElements.define(
    'tt-sticky-atc',
    class TTStickyAtc extends HTMLElement {
      connectedCallback() {
        const target = document.querySelector('[data-tt-atc]');
        if (!target || !('IntersectionObserver' in window)) return;
        const btn = this.querySelector('button');
        new IntersectionObserver(([e]) => {
          const show = !e.isIntersecting && e.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', !show);
          if (btn) btn.tabIndex = show ? 0 : -1;
        }).observe(target);
      }
    }
  );
}

/* ---------------- Product page: purchase bar (qty stepper, live total, added state) ---------------- */
const ttMoney = (cents, currency) => {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(cents / 100);
  } catch (e) {
    return '£' + (cents / 100).toFixed(2);
  }
};
const ttSyncPurchase = (bar) => {
  if (!bar) return;
  const input = bar.querySelector('input[name=quantity]');
  const max = parseInt(input.max, 10) || Infinity;
  let q = parseInt(input.value, 10);
  if (!Number.isFinite(q) || q < 1) q = 1;
  if (q > max) q = max;
  input.value = q;
  const [minus, plus] = bar.querySelectorAll('[data-step]');
  if (minus) minus.disabled = q <= 1;
  if (plus) plus.disabled = q >= max;
  const price = parseInt(bar.dataset.price, 10) || 0;
  const total = bar.querySelector('[data-atc-total]');
  if (total) total.textContent = ttMoney(price * q, bar.dataset.currency);
  const text = bar.querySelector('[data-atc-text]');
  if (text) text.textContent = q > 1 ? `Add ${q} to basket` : 'Add to basket';
  const hint = bar.parentElement.querySelector('[data-qty-hint]');
  if (hint) hint.hidden = !(Number.isFinite(max) && q >= max && max > 1);
  const sticky = document.querySelector('.tt-sticky__price');
  if (sticky) sticky.textContent = ttMoney(price * q, bar.dataset.currency);
};
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-tt-qty] [data-step]');
  if (!b) return;
  const bar = b.closest('[data-tt-purchase]');
  const input = b.closest('[data-tt-qty]').querySelector('input');
  input.value = (parseInt(input.value, 10) || 1) + parseInt(b.dataset.step, 10);
  if (bar) ttSyncPurchase(bar);
  else {
    const max = parseInt(input.max, 10) || Infinity;
    input.value = Math.min(max, Math.max(1, parseInt(input.value, 10)));
  }
});
document.addEventListener('input', (e) => {
  if (e.target.matches('[data-tt-purchase] input[name=quantity]')) ttSyncPurchase(e.target.closest('[data-tt-purchase]'));
});
document.addEventListener('change', (e) => {
  if (e.target.matches('[data-tt-purchase] input[name=quantity]')) ttSyncPurchase(e.target.closest('[data-tt-purchase]'));
});
document.addEventListener('DOMContentLoaded', () => document.querySelectorAll('[data-tt-purchase]').forEach(ttSyncPurchase));

// "Added to basket" confirmation after a successful add from the product page
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (!event || event.source !== 'product-form') return;
    document.querySelectorAll('.tt-purchase__atc').forEach((btn) => {
      btn.classList.add('is-added');
      clearTimeout(btn._ttAdded);
      btn._ttAdded = setTimeout(() => btn.classList.remove('is-added'), 2200);
    });
  });
}

/* ---------------- Product page: simple variant select ---------------- */
document.addEventListener('change', (e) => {
  const sel = e.target.closest('[data-tt-variant]');
  if (!sel) return;
  const opt = sel.selectedOptions[0];
  const form = sel.closest('form');
  form.querySelector('[name=id]').value = opt.value;
  const section = sel.closest('.tt-pdp');
  const cents = parseInt(opt.dataset.cents, 10);
  section.querySelectorAll('.tt-price--lg').forEach((el) => {
    const [i, d] = (cents / 100).toFixed(2).split('.');
    const int = el.querySelector('.tt-price__int'), dec = el.querySelector('.tt-price__dec');
    if (int) int.textContent = Number(i).toLocaleString('en-GB');
    if (dec) dec.textContent = '.' + d;
  });
  const ok = opt.dataset.available === 'true';
  section.querySelectorAll('.tt-atc').forEach((btn) => (btn.disabled = !ok));
  const bar = section.querySelector('[data-tt-purchase]');
  if (bar && opt.dataset.cents) { bar.dataset.price = opt.dataset.cents; ttSyncPurchase(bar); }
  const url = new URL(window.location.href);
  url.searchParams.set('variant', opt.value);
  history.replaceState({}, '', url);
});
